import {
    downstreamVenuesTqo,
    NewWasteFood,
    Unit,
    useBundleById,
    useWasteFlavorById,
    VenueStub,
    Waste,
    WasteFlavor,
    WasteFlavorStub,
    WastePatch,
    WasteStampFlavor
} from "../rpc/webrpc-client.gen.ts";
import {
    Control,
    Controller,
    FieldErrors,
    useController,
    useForm,
    UseFormClearErrors,
    UseFormGetValues,
    UseFormSetValue,
    useWatch
} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {
    Box,
    Button,
    Dropdown,
    FormControl,
    FormLabel,
    IconButton,
    Input as JoyInput,
    ListItemDecorator,
    Menu,
    MenuButton,
    MenuItem,
    Modal,
    Sheet,
    Stack,
    ToggleButtonGroup,
    Typography
} from "@mui/joy";
import {faCheck, faLink, faLinkSlash} from "@fortawesome/pro-regular-svg-icons";
import {SxProps} from "@mui/joy/styles/types";
import {NumericFormat} from "react-number-format";
import {EditorMode, ReactHookFormValidationErrorAlert} from "../forms.tsx";
import {useOrientation} from "../mq.ts";
import {useQuery} from "@tanstack/react-query";
import {faCaretDown} from "@fortawesome/pro-solid-svg-icons";
import {FormEvent, useCallback, useEffect, useMemo, useState} from "react";
import {WasteFlavorChooser} from "./flavorChooser.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {HCBox} from "../components/HCBox.tsx";
import {useSelf} from "../agent/useSelf.ts";
import {useBundleContinuingWastes, useBundleWasteDefaults} from "../bundle/bundle.tsx";
import {isEmpty} from "lodash";
import {
    useManifestTypeArmap,
    useMothership,
    usePackingModeArmap,
    usePatchWasteMutation,
    useSpawnWasteMutation,
    useStampWasteMutation,
    useUnitArmap
} from "../tq/tqx.tsx";
import {z} from "zod/v4";
import {fixedPointToStr, strToFixedPoint} from "../tq/tqutil.tsx";
import {useBomb} from "../bomb/bomb.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {Armap} from "../util/arutil.tsx";

export interface WasteEditorProps {
    bundleId: string;
    mode?: EditorMode;
    waste?: Waste;
    // leave undefined and you get whatever flavors generally get shown
    // to you. main use for this is splitting wastes, as only certain
    // classes can be legal mothers and daughters
    possibleFlavors?: WasteFlavorStub[];
    onFlavorChange?: (newFlavor: WasteFlavorStub) => void;
    // these only fire if you leave onValidSubmit undefined
    afterSpawn?: ({waste}: { waste: Waste }) => void;
    afterPatch?: () => void;
    onValidSubmit?: (form: WasteForm) => Promise<unknown>;
}

export const WasteForm = z.object({
    id: z.optional(z.string()),

    flavor: z.nullable(WasteFlavorStub),
    invManiLinked: z.boolean(),
    packingModeId: z.nullable(z.string()),
    manifestTypeId: z.nullable(z.string()),

    invQty: z.string(),
    maniQty: z.string(),
    invKg: z.string(),
    maniKg: z.string(),
    invVolm3: z.string(),
    maniVolm3: z.string(),
    manifestNumber: z.string(),
    bara: z.string(),
    bags: z.string(),

    destination: z.nullable(VenueStub),
});

// export interface WasteForm {
//     id?: string;
//
//     flavor: WasteFlavorStub | null;
//     invManiLinked: boolean;
//     packingMode: PackingMode | null;
//     manifestType: ManifestType | null;
//
//     invQty: string;
//     maniQty: string;
//     invKg: string;
//     maniKg: string;
//     invVolm3: string;
//     maniVolm3: string;
//     manifestNumber: string;
//     bara: string;
//     bags: string;
//
//     destination: VenueStub | null;
// }
export type WasteForm = z.infer<typeof WasteForm>;

export function wasteToWasteForm(w: Waste): WasteForm {
    const rv: WasteForm = {
        id: w.id,
        flavor: w.flavor,
        invManiLinked: w.isInvManiLinked,
        invQty: fixedPointToStr(w.invQ, 1),
        invVolm3: fixedPointToStr(w.invV, 1),
        invKg: fixedPointToStr(w.invW, 3),
        maniQty: fixedPointToStr(w.maniQ, 1),
        maniVolm3: fixedPointToStr(w.maniV, 1),
        maniKg: fixedPointToStr(w.maniW, 3),
        packingModeId: w.packingModeId ?? null,
        manifestTypeId: w.manifestTypeId ?? null,
        manifestNumber: w.manifestNumber ?? "",
        bara: fixedPointToStr(w.bara0, 1),
        bags: fixedPointToStr(w.bags, 0),
        destination: w.destination ?? null,
    };

    return rv;
}

export function wasteFormToNewWasteFood(f: WasteForm): NewWasteFood {
    if (f.id) {
        throw new Error("should be patching, get out of here");
    }

    const rv: NewWasteFood = {
        flavorId: f.flavor!.id,
        isInvManiLinked: f.invManiLinked,
        invQ: strToFixedPoint(f.invQty, 1),
        maniQ: strToFixedPoint(f.maniQty, 1),
        invW: strToFixedPoint(f.invKg, 3),
        maniW: strToFixedPoint(f.maniKg, 3),
        invV: strToFixedPoint(f.invVolm3, 1),
        maniV: strToFixedPoint(f.maniVolm3, 1),
        bara0: strToFixedPoint(f.bara, 1),
        packQ: strToFixedPoint(f.bags, 0),
        bags: strToFixedPoint(f.bags, 0),
    }

    if (f.packingModeId) {
        rv.packingModeId = f.packingModeId;
    }
    if (f.manifestTypeId) {
        rv.manifestTypeId = f.manifestTypeId;
    }

    if (f.manifestNumber) {
        rv.manifestNumber = f.manifestNumber;
    }

    return rv;
}

export function makeWasteFormPatch({prev, next}: { prev: WasteForm, next: WasteForm }): WastePatch {
    const rv: WastePatch = {};
    if (prev.flavor?.id !== next.flavor!.id) {
        rv.flavorId = next.flavor!.id;
    }

    if (prev.invManiLinked !== next.invManiLinked) {
        rv.isInvManiLinked = next.invManiLinked;
    }

    if (prev.invQty !== next.invQty) {
        rv.invQ = strToFixedPoint(next.invQty, 1);
    }

    if (prev.invVolm3 !== next.invVolm3) {
        rv.invV = strToFixedPoint(next.invVolm3, 1);
    }
    if (prev.invKg !== next.invKg) {
        rv.invW = strToFixedPoint(next.invKg, 3);
    }
    if (prev.maniQty !== next.maniQty) {
        rv.maniQ = strToFixedPoint(next.maniQty, 1);
    }
    if (prev.maniVolm3 !== next.maniVolm3) {
        rv.maniV = strToFixedPoint(next.maniVolm3, 1);
    }
    if (prev.maniKg !== next.maniKg) {
        rv.maniW = strToFixedPoint(next.maniKg, 3);
    }
    if (prev.packingModeId !== next.packingModeId) {
        rv.packingModeId = next.packingModeId ?? "";
    }
    if (prev.bara !== next.bara) {
        rv.bara0 = strToFixedPoint(next.bara, 1);
    }
    if (prev.bags !== next.bags) {
        rv.bags = strToFixedPoint(next.bags, 0);
        rv.packQ = rv.bags
    }
    if (prev.manifestTypeId !== next.manifestTypeId) {
        rv.manifestTypeId = next.manifestTypeId ?? "";
    }
    // this way of writing it lets null and undefined and ""
    // all be considered interchangeable
    if (!prev.manifestNumber !== !next.manifestNumber && prev.manifestNumber !== next.manifestNumber) {
        rv.manifestNumber = next.manifestNumber;
    }

    // TODO destination

    return rv;
}

export function makeWastePatch({prev, next}: { prev: Waste, next: WasteForm }): WastePatch {
    const rv: WastePatch = {};
    if (prev.flavor.id !== next.flavor!.id) {
        rv.flavorId = next.flavor!.id;
    }
    if (prev.isInvManiLinked !== next.invManiLinked) {
        rv.isInvManiLinked = next.invManiLinked;
    }
    const nextInvQty0 = strToFixedPoint(next.invQty, 1);
    if (prev.invQ !== nextInvQty0) {
        rv.invQ = nextInvQty0;
    }
    const nextInvVoldm3 = strToFixedPoint(next.invVolm3, 1);
    if (prev.invV !== nextInvVoldm3) {
        rv.invV = nextInvVoldm3;
    }
    const nextInvGram = strToFixedPoint(next.invKg, 3);
    if (prev.invW !== nextInvGram) {
        rv.invW = nextInvGram;
    }
    const nextManiQty0 = strToFixedPoint(next.maniQty, 1);
    if (prev.maniQ !== nextManiQty0) {
        rv.maniQ = nextManiQty0;
    }
    const nextManiVoldm3 = strToFixedPoint(next.maniVolm3, 1);
    if (prev.maniV !== nextManiVoldm3) {
        rv.maniV = nextManiVoldm3;
    }
    const nextManiGram = strToFixedPoint(next.maniKg, 3);
    if (prev.maniW !== nextManiGram) {
        rv.maniW = nextManiGram;
    }
    if (prev.packingModeId !== next.packingModeId) {
        rv.packingModeId = next.packingModeId ?? "";
    }
    const nextBara0 = strToFixedPoint(next.bara, 1);
    if (prev.bara0 !== nextBara0) {
        rv.bara0 = nextBara0;
    }
    const nextBags = strToFixedPoint(next.bags, 0);
    if (prev.bags !== nextBags) {
        rv.bags = nextBags;
    }
    if (prev.manifestTypeId !== next.manifestTypeId) {
        rv.manifestTypeId = next.manifestTypeId ?? "";
    }
    // this way of writing it lets null and undefined and ""
    // all be considered interchangeable
    if (!prev.manifestNumber !== !next.manifestNumber && prev.manifestNumber !== next.manifestNumber) {
        rv.manifestNumber = next.manifestNumber;
    }

    if ((!prev.destination && !!next.destination)
        || (!!prev.destination && !!next.destination
            && prev.destination.id !== next.destination.id)) {
        rv.destinationId = next.destination.id;
    }

    return rv;
}

function FlavorField({control, readonly, setFlavorChooserOpen}: {
    control: Control<WasteForm>,
    readonly: boolean,
    setFlavorChooserOpen: (isOpen: boolean) => void,
}) {
    const {t} = useTranslation();
    if (readonly) {
        return null;
    }

    return <Controller control={control} name="flavor"
                       render={() =>
                           <Button variant="soft" sx={{px: 4, py: 0.6}}
                                   onClick={() => {
                                       setFlavorChooserOpen(true);
                                   }}>
                               {t("flavorChange")}
                           </Button>
                       }/>

    // return <Stack direction="row" sx={{alignItems: "center"}} spacing={2}>
    //     <Box sx={{flex: 1}}/>
    //
    //     <Typography level="title-lg">{t("flavorTitle")}</Typography>
    //     <Controller control={control} name="flavor"
    //                 render={({field: {value}}) => {
    //                     return <Box>{value
    //                         ? value.displayName
    //                         : t("unset")}</Box>
    //                 }}/>
    //     <Box sx={{fontSize: "large"}}></Box>
    //     <Box sx={{flex: 1, display: "flex", alignItems: "center"}}>
    //         <Divider orientation="horizontal" sx={{
    //             flex: 1, bgcolor: "primary.400", mx: "1rem"
    //         }}/>
    //     </Box>
    //     {readonly
    //         ? null
    //         : <IconButton color="primary" variant="solid" onClick={() => {
    //             setFlavorChooserOpen(true);
    //         }}><FontAwesomeIcon icon={faPencil}/></IconButton>}
    // </Stack>
}

const VOLUME_UNIT_CODE = "2";
const WEIGHT_UNIT_CODE = "3";

function InvoiceQuantityField({control, readonly, isMandatory, updateLink, flavor, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    updateLink?: (nv: string) => void,
    flavor: WasteFlavor | undefined,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();
    const {data: units} = useUnitArmap();

    const iuid = flavor?.invoiceUnitId;
    const invoiceUnitName = iuid ? units?.byId.get(iuid)?.displayName : "";

    return <Box sx={{
        display: isMandatory ? "block" : "none",
    }}>
        <Controller<WasteForm, "invQty">
            control={control}
            name="invQty"
            render={({field,}) =>
                <FormControl
                    sx={{
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...sx,
                        ...(formErrors.invQty ? {borderColor: "danger.300"} : {})
                    }}
                    error={!!formErrors.invQty}>
                    <FormLabel>{t("quantity")}</FormLabel>
                    <NumericFormat
                        decimalScale={3}
                        value={field.value}
                        onValueChange={(values) => {
                            field.onChange({
                                target: {
                                    name: field.name,
                                    value: values.value,
                                }
                            });
                            if (updateLink) {
                                updateLink(values.value);
                            }
                        }}
                        customInput={JoyInput}
                        slotProps={{input: {type: "number"}}}
                        sx={{maxWidth: "8rem"}}
                        valueIsNumericString={true}
                        endDecorator={invoiceUnitName}
                        readOnly={readonly}
                    />
                    <ReactHookFormValidationErrorAlert error={formErrors.invQty}/>
                </FormControl>
            }
            rules={{required: isMandatory}}/>
    </Box>
}

function InvoiceVolumeField({control, readonly, isMandatory, updateLink, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    updateLink?: (nv: string) => void,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();

    return <Controller<WasteForm, "invVolm3">
        control={control}
        name="invVolm3"
        render={({field}) =>
            <FormControl
                sx={{
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...sx,
                    ...(formErrors.invVolm3 ? {borderColor: "danger.300"} : {})
                }}
                error={!!formErrors.invVolm3}>
                <FormLabel>{t("volume")}</FormLabel>
                <NumericFormat
                    decimalScale={1}
                    value={field.value}
                    onValueChange={(values) => {
                        field.onChange({
                            target: {
                                name: field.name,
                                value: values.value,
                            }
                        });
                        if (updateLink) {
                            updateLink(values.value);
                        }
                    }}
                    customInput={JoyInput}
                    slotProps={{input: {type: "number"}}}
                    sx={{maxWidth: "8rem"}}
                    valueIsNumericString={true}
                    endDecorator={t("volumeUnit")}
                    readOnly={readonly}
                />
                <ReactHookFormValidationErrorAlert error={formErrors.invVolm3}/>
            </FormControl>
        }
        rules={{required: isMandatory}}
    />
}

function InvoiceWeightField({control, readonly, isMandatory, updateLink, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    updateLink?: (nv: string) => void,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();
    return <Controller<WasteForm, "invKg">
        control={control}
        name="invKg"
        render={({field}) =>
            <FormControl
                sx={{
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...sx,
                    ...(formErrors.invKg ? {borderColor: "danger.300"} : {})
                }}
                error={!!formErrors.invKg}>
                <FormLabel>{t("weight")}</FormLabel>
                <NumericFormat
                    decimalScale={3}
                    value={field.value}
                    onValueChange={(values) => {
                        field.onChange({
                            target: {
                                name: field.name,
                                value: values.value,
                            }
                        });
                        if (updateLink) {
                            updateLink(values.value);
                        }
                    }}
                    customInput={JoyInput}
                    slotProps={{input: {type: "number"}}}
                    sx={{maxWidth: "8rem"}}
                    valueIsNumericString={true}
                    endDecorator={t("weightUnit")}
                    readOnly={readonly}
                />
                <ReactHookFormValidationErrorAlert error={formErrors.invKg}/>
            </FormControl>}
        rules={{required: isMandatory}}
    />
}

function InvoiceFieldset({control, readonly, formErrors, flavor, updateLink, mandatorySx, commonFieldSx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    formErrors: FieldErrors<WasteForm>,
    flavor: WasteFlavor | undefined,
    updateLink?: (nv: string) => void,
    mandatorySx: SxProps,
    commonFieldSx: SxProps,
}) {
    const {t} = useTranslation();
    const {data: units} = useUnitArmap();
    const iunit = units?.byId.get(flavor?.invoiceUnitId ?? "");
    const isVolMandatory = iunit?.code === VOLUME_UNIT_CODE;
    const isWeightMandatory = iunit?.code === WEIGHT_UNIT_CODE;
    const isQtyMandatory = !isVolMandatory && !isWeightMandatory;
    const volSx = isVolMandatory ? mandatorySx : commonFieldSx;
    const weightSx = isWeightMandatory ? mandatorySx : commonFieldSx;
    const qtySx = isQtyMandatory ? mandatorySx : commonFieldSx;
    const direction = useOrientation() === "landscape" ? "column" : "row";

    return <Stack component="fieldset" disabled={readonly}
                  direction={direction}>
        <legend>{t("invoiceFieldset")}</legend>
        <InvoiceQuantityField control={control} readonly={readonly}
                              formErrors={formErrors} flavor={flavor}
                              sx={qtySx} isMandatory={isQtyMandatory}
                              updateLink={isQtyMandatory ? updateLink : undefined}/>
        <InvoiceVolumeField control={control} readonly={readonly}
                            formErrors={formErrors}
                            sx={volSx} isMandatory={isVolMandatory}
                            updateLink={isVolMandatory ? updateLink : undefined}/>
        <InvoiceWeightField control={control} readonly={readonly}
                            formErrors={formErrors}
                            sx={weightSx} isMandatory={isWeightMandatory}
                            updateLink={isWeightMandatory ? updateLink : undefined}/>
    </Stack>
}

function InvManiLinkField({value, onChange, readonly, clearErrors}: {
    value: boolean,
    onChange: (evt: { target: { value: boolean } }) => void,
    readonly: boolean,
    clearErrors: UseFormClearErrors<WasteForm>,
}) {
    const {t} = useTranslation();
    const orientation = useOrientation();
    return readonly
        ? (value
            ? <Typography>{t("inmaniLinked")}</Typography>
            : null)
        : <ToggleButtonGroup
            variant="plain"
            orientation={orientation === "landscape" ? "vertical" : "horizontal"}
            color="primary"
            value={value ? "linked" : "unlinked"}
            onChange={(_evt, nv) => {
                onChange({
                    target: {
                        value: nv !== "unlinked",
                    }
                });
                clearErrors();
            }}
        >
            <IconButton value="linked">
                <FontAwesomeIcon icon={faLink}/>
            </IconButton>
            <IconButton value="unlinked">
                <FontAwesomeIcon icon={faLinkSlash}/>
            </IconButton>
        </ToggleButtonGroup>
}

function ManifestQuantityField({control, readonly, isMandatory, flavor, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    flavor: WasteFlavor | undefined,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();
    const {data: units} = useUnitArmap();

    const muid = flavor?.manifestUnitId;
    const manifestUnitName = muid ? units?.byId.get(muid)?.displayName : "";

    return <Box sx={{
        display: isMandatory ? "block" : "none",
    }}>
        <Controller<WasteForm, "maniQty">
            control={control}
            name="maniQty"
            render={({field,}) =>
                <FormControl
                    sx={{
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...sx,
                        ...(formErrors.maniQty ? {borderColor: "danger.300"} : {})
                    }}
                    error={!!formErrors.maniQty}>
                    <FormLabel>{t("quantity")}</FormLabel>
                    <NumericFormat
                        decimalScale={3}
                        value={field.value}
                        onValueChange={(values) => {
                            field.onChange({
                                target: {
                                    name: field.name,
                                    value: values.value,
                                }
                            });
                        }}
                        customInput={JoyInput}
                        sx={{maxWidth: "8rem"}}
                        slotProps={{input: {type: "number"}}}
                        valueIsNumericString={true}
                        endDecorator={manifestUnitName}
                        readOnly={readonly}
                    />
                    <ReactHookFormValidationErrorAlert error={formErrors.maniQty}/>
                </FormControl>
            }
            rules={{required: isMandatory}}/>
    </Box>
}

function ManifestVolumeField({control, readonly, isMandatory, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();

    return <Controller<WasteForm, "maniVolm3">
        control={control}
        name="maniVolm3"
        render={({field}) =>
            <FormControl
                sx={{
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...sx,
                    ...(formErrors.maniVolm3 ? {borderColor: "danger.300"} : {})
                }}
                error={!!formErrors.maniVolm3}>
                <FormLabel>{t("volume")}</FormLabel>
                <NumericFormat
                    decimalScale={1}
                    value={field.value}
                    onValueChange={(values) => {
                        field.onChange({
                            target: {
                                name: field.name,
                                value: values.value,
                            }
                        });
                    }}
                    customInput={JoyInput}
                    sx={{maxWidth: "8rem"}}
                    slotProps={{input: {type: "number"}}}
                    valueIsNumericString={true}
                    endDecorator={t("volumeUnit")}
                    readOnly={readonly}
                />
                <ReactHookFormValidationErrorAlert error={formErrors.maniVolm3}/>
            </FormControl>
        }
        rules={{required: isMandatory}}
    />
}

function ManifestWeightField({control, readonly, isMandatory, formErrors, sx}: {
    control: Control<WasteForm>,
    readonly: boolean,
    isMandatory: boolean,
    formErrors: FieldErrors<WasteForm>,
    sx: SxProps,
}) {
    const {t} = useTranslation();
    return <Controller<WasteForm, "maniKg">
        control={control}
        name="maniKg"
        render={({field}) =>
            <FormControl
                sx={{
                    // eslint-disable-next-line @typescript-eslint/no-misused-spread
                    ...sx,
                    ...(formErrors.maniKg ? {borderColor: "danger.300"} : {})
                }}
                error={!!formErrors.maniKg}>
                <FormLabel>{t("weight")}</FormLabel>
                <NumericFormat
                    decimalScale={3}
                    value={field.value}
                    onValueChange={(values) => {
                        field.onChange({
                            target: {
                                name: field.name,
                                value: values.value,
                            }
                        });
                    }}
                    customInput={JoyInput}
                    sx={{maxWidth: "8rem"}}
                    slotProps={{input: {type: "number"}}}
                    valueIsNumericString={true}
                    endDecorator={t("weightUnit")}
                    readOnly={readonly}
                />
                <ReactHookFormValidationErrorAlert error={formErrors.maniKg}/>
            </FormControl>}
        rules={{required: isMandatory}}
    />
}

function ManifestFieldset({
                              control, formErrors, flavor, disabled,
                              mandatorySx, commonFieldSx
                          }: {
    control: Control<WasteForm>,
    formErrors: FieldErrors<WasteForm>,
    flavor: WasteFlavor | undefined,
    disabled: boolean,
    isInvManiLinked: boolean,
    mandatorySx: SxProps,
    commonFieldSx: SxProps,
}) {
    const {t} = useTranslation();
    const {data: units} = useUnitArmap();
    const munit = units?.byId.get(flavor?.manifestUnitId ?? "");

    const isVolMandatory = !disabled && munit?.code === VOLUME_UNIT_CODE;
    const isWeightMandatory = !disabled && munit?.code === WEIGHT_UNIT_CODE;
    const isQtyMandatory = !disabled && !isVolMandatory && !isWeightMandatory;
    const volSx = isVolMandatory ? mandatorySx : commonFieldSx;
    const weightSx = isWeightMandatory ? mandatorySx : commonFieldSx;
    const qtySx = isQtyMandatory ? mandatorySx : commonFieldSx;
    const direction = useOrientation() === "landscape" ? "column" : "row";

    return <Stack component="fieldset" disabled={disabled}
                  direction={direction}>
        <legend>{t("manifestFieldset")}</legend>
        <ManifestQuantityField control={control} readonly={disabled}
                               formErrors={formErrors} flavor={flavor}
                               sx={qtySx} isMandatory={isQtyMandatory}/>
        <ManifestVolumeField control={control} readonly={disabled}
                             formErrors={formErrors}
                             sx={volSx} isMandatory={isVolMandatory}/>
        <ManifestWeightField control={control} readonly={disabled}
                             formErrors={formErrors}
                             sx={weightSx} isMandatory={isWeightMandatory}/>
    </Stack>
}

function PackingModeField({control, readonly}: {
    control: Control<WasteForm>,
    readonly: boolean,
}) {
    const {t} = useTranslation();
    const {data: packingModes} = usePackingModeArmap();
    if (!packingModes) {
        return null;
    }

    return <Controller
        control={control}
        name="packingModeId"
        render={({field}) => <FormControl>
            <FormLabel>{t("entity.packingMode")}</FormLabel>
            {readonly
                ? <Typography>{field.value ? packingModes.byId.get(field.value)?.displayName : t("unset")}
                </Typography>
                : <Dropdown>
                    <MenuButton endDecorator={<FontAwesomeIcon icon={faCaretDown}/>}>
                        {field.value ? packingModes.byId.get(field.value)?.displayName : t("unset")}
                    </MenuButton>
                    <Menu placement="bottom"
                          sx={{
                              minWidth: 160,
                              "--ListItemDecorator-size": "24px",
                              zIndex: "1350"
                          }}>
                        {packingModes.ar.map(packingMode =>
                            <MenuItem key={packingMode.id}
                                      onClick={() => {
                                          field.onChange({
                                              target: {
                                                  name: field.name,
                                                  value: packingMode.id,
                                              }
                                          })
                                      }}>
                                <ListItemDecorator>{packingMode.id === field.value
                                    && <FontAwesomeIcon icon={faCheck}/>}
                                </ListItemDecorator>
                                {packingMode.displayName}
                            </MenuItem>)}
                    </Menu>
                </Dropdown>}
        </FormControl>
        }/>
}

function ManifestTypeField({control, readonly}: {
    control: Control<WasteForm>,
    readonly: boolean,
}) {
    const {t} = useTranslation();
    const {data: manifestTypes} = useManifestTypeArmap();
    if (!manifestTypes) {
        return null;
    }

    return <Controller
        control={control}
        name="manifestTypeId"
        render={({field}) => <FormControl>
            <FormLabel>{t("entity.manifestType")}</FormLabel>
            {readonly
                ? <Typography>{field.value ? manifestTypes.byId.get(field.value)?.displayName : t("unset")}</Typography>
                : <Dropdown>
                    <MenuButton endDecorator={<FontAwesomeIcon icon={faCaretDown}/>}>
                        {field.value ? manifestTypes.byId.get(field.value)?.displayName : t("unset")}
                    </MenuButton>
                    <Menu placement="top"
                          sx={{
                              minWidth: 160,
                              "--ListItemDecorator-size": "24px",
                              zIndex: "1350"
                          }}>
                        {manifestTypes.ar.map(manifestType =>
                            <MenuItem key={manifestType.id}
                                      onClick={() => {
                                          field.onChange({
                                              target: {
                                                  name: field.name,
                                                  value: manifestType.id,
                                              }
                                          })
                                      }}>
                                <ListItemDecorator>{manifestType.id === field.value
                                    && <FontAwesomeIcon icon={faCheck}/>}
                                </ListItemDecorator>
                                {manifestType.displayName}
                            </MenuItem>)}
                    </Menu>
                </Dropdown>}
        </FormControl>
        }/>
}

function ManifestNumberField({control, readonly}: {
    control: Control<WasteForm>,
    readonly: boolean,
}) {
    const {t} = useTranslation();
    return <Controller<WasteForm, "manifestNumber">
        control={control}
        name="manifestNumber"
        render={({field,}) => <FormControl>
            <FormLabel>{t("manifestNumber")}</FormLabel>
            <JoyInput value={field.value} onChange={field.onChange}
                      sx={{maxWidth: "8rem"}}
                      disabled={readonly}
            />
        </FormControl>}/>
}

function BaraField({control, readonly}: {
    control: Control<WasteForm>,
    readonly: boolean,
}) {
    const {t} = useTranslation();
    return <Controller<WasteForm, "bara">
        control={control}
        name="bara"
        render={({field,}) => <FormControl>
            <FormLabel>{t("bara")}</FormLabel>
            <NumericFormat
                decimalScale={1}
                value={field.value}
                onValueChange={(values) => {
                    field.onChange({
                        target: {
                            name: field.name,
                            value: values.value,
                        }
                    });
                }}
                customInput={JoyInput}
                sx={{maxWidth: "8rem"}}
                valueIsNumericString={true}
                readOnly={readonly}
            />
        </FormControl>}/>

}

function BagsField({control, readonly}: {
    control: Control<WasteForm>,
    readonly: boolean,
}) {
    const {t} = useTranslation();
    return <Controller<WasteForm, "bags">
        control={control}
        name="bags"
        render={({field}) => <FormControl>
            <FormLabel>{t("bags")}</FormLabel>
            <NumericFormat
                decimalScale={0}
                value={field.value}
                onValueChange={(values) => {
                    field.onChange({
                        target: {
                            name: field.name,
                            value: values.value,
                        }
                    });
                }}
                customInput={JoyInput}
                slotProps={{input: {type: "number"}}}
                sx={{maxWidth: "8rem"}}
                valueIsNumericString={true}
                readOnly={readonly}
            />
        </FormControl>}/>
}

// interface InvFieldSpec {
//     name: "invVolm3" | "invKg" | "invQty",
//     decimalScale: number
// }

// const INV_VOL_SPEC: InvFieldSpec = {name: "invVolm3", decimalScale: 1};
// const INV_WEIGHT_SPEC: InvFieldSpec = {name: "invKg", decimalScale: 3};
// const INV_QTY_SPEC: InvFieldSpec = {name: "invQty", decimalScale: 1};

// function mandatoryInvFieldSpec(flavor: WasteFlavor): InvFieldSpec {
//     if (flavor.invoiceUnit === i18next.t("volumeUnit")) {
//         return INV_VOL_SPEC;
//     }
//     if (flavor.invoiceUnit === i18next.t("weightUnit")) {
//         return INV_WEIGHT_SPEC;
//     }
//     return INV_QTY_SPEC;
// }

interface ManiFieldSpec {
    name: "maniVolm3" | "maniKg" | "maniQty",
    decimalScale: number
}

const MANI_VOL_SPEC: ManiFieldSpec = {name: "maniVolm3", decimalScale: 1};
const MANI_WEIGHT_SPEC: ManiFieldSpec = {name: "maniKg", decimalScale: 3};
const MANI_QTY_SPEC: ManiFieldSpec = {name: "maniQty", decimalScale: 1};

function mandatoryManiFieldSpec(flavor: WasteFlavorStub, units?: Armap<Unit>): ManiFieldSpec {
    const munit = units?.byId.get(flavor.manifestUnitId ?? "");
    if (munit?.code === VOLUME_UNIT_CODE) {
        return MANI_VOL_SPEC;
    }
    if (munit?.code === WEIGHT_UNIT_CODE) {
        return MANI_WEIGHT_SPEC;
    }
    return MANI_QTY_SPEC;
}

interface UpdateLinkProps {
    flavor: WasteFlavor;
    invFieldValueStr: string;
    units?: Armap<Unit>;
    setFormValue: UseFormSetValue<WasteForm>;
    getFormValues: UseFormGetValues<WasteForm>;
}

function updateLink({flavor, invFieldValueStr, units, setFormValue, getFormValues}: UpdateLinkProps) {
    const maniFieldSpec = mandatoryManiFieldSpec(flavor, units);
    const invFieldValue = Number.parseFloat(invFieldValueStr);
    const maniFieldValueStr
        = getFormValues(maniFieldSpec.name);
    const m = flavor.invoiceManiSlope;
    const b = flavor.invoiceManiIntercept;

    const calcedManiFieldValue = invFieldValue ? (invFieldValue * m + b) : null;
    const fixedCalcedManiFieldValue = calcedManiFieldValue
        ? calcedManiFieldValue * Math.pow(10, maniFieldSpec.decimalScale)
        : null;
    const calcedManiFieldValueStr
        = fixedPointToStr(fixedCalcedManiFieldValue, maniFieldSpec.decimalScale);
    if (calcedManiFieldValueStr !== maniFieldValueStr) {
        setFormValue(maniFieldSpec.name, calcedManiFieldValueStr);
    }
}

function DestinationField({control, readonly, bundleDestination}: {
    control: Control<WasteForm>,
    readonly: boolean,
    bundleDestination?: VenueStub,
}) {
    const {t} = useTranslation();
    const mama = useMothership();
    const {data: possibleDestinationsData} = useQuery(downstreamVenuesTqo(mama));
    const possibleDestinations = useMemo(() => {
        const rv = [];
        if (bundleDestination) {
            rv.push(bundleDestination);
        }
        if (possibleDestinationsData) {
            rv.push(...possibleDestinationsData);
        }
        return rv;
    }, [bundleDestination, possibleDestinationsData]);
    // const possibleDestinations: VenueStub[] = [
    //     {id: "R1", code: "900005", displayName: "仙台環境"},
    //     {id: "R2", code: "900037", displayName: "クリショー"},
    //     {id: "R3", code: "900148", displayName: "木幡興業"},
    //     {id: "R4", code: "900029", displayName: "柏廃材"},
    //     {id: "R5", code: "900602", displayName: "ウムヴェルトジャパン"},
    // ];

    return <Controller
        control={control}
        name="destination"
        render={({field}) => {
            return <FormControl>
                <FormLabel>{t("inspection.wasteDestinationLabel")}</FormLabel>
                {readonly
                    ? <Typography>{field.value ? field.value.displayName : t("unset")}
                    </Typography>
                    : <Dropdown>
                        <MenuButton endDecorator={<FontAwesomeIcon icon={faCaretDown}/>}>
                            {field.value ? field.value.displayName : t("unset")}
                        </MenuButton>
                        <Menu placement="bottom"
                              sx={{
                                  minWidth: 160,
                                  "--ListItemDecorator-size": "24px",
                                  zIndex: "1350"
                              }}>
                            {possibleDestinations.map(stub =>
                                <MenuItem key={stub.id}
                                          onClick={() => {
                                              field.onChange({
                                                  target: {
                                                      name: field.name,
                                                      value: stub,
                                                  }
                                              })
                                          }}>
                                    <ListItemDecorator>{stub === field.value
                                        ? <FontAwesomeIcon icon={faCheck}/> : null}
                                    </ListItemDecorator>
                                    {stub.displayName}
                                </MenuItem>)}
                        </Menu>
                    </Dropdown>}
            </FormControl>
        }}/>
}

// bundleId is tricky. it's needed in order for this component to be able
// to spawn wastes from scratch, but unavailable for wastes that are unaffiliated
// with a bundle.

export function WasteEditor(props: WasteEditorProps) {
    const {t} = useTranslation();
    const {
        mode: inMode,
        bundleId,
        waste,
        possibleFlavors: possibleFlavorsIn,
        onFlavorChange,
        afterSpawn,
        afterPatch,
        onValidSubmit,
    } = props;
    const readonly = inMode !== "rw";

    const {
        handleSubmit,
        control,
        clearErrors,
        formState: {
            isDirty: isFormDirty,
            errors: formErrors,
            isValid: isFormValid,
            // ...formStateRest
        },
        getValues: getFormValues,
        setValue: setFormValue,
        reset: resetForm,
    } = useForm<WasteForm>({
        mode: "onChange",
        defaultValues: waste
            ? wasteToWasteForm(waste)
            : {
                flavor: null,
                invManiLinked: true,
                packingModeId: null,
                manifestTypeId: null,

                invQty: "",
                maniQty: "",
                invKg: "",
                maniKg: "",
                invVolm3: "",
                maniVolm3: "",
                manifestNumber: "",
                bara: "",
                bags: "",
            }
    });

    const {data: units} = useUnitArmap();
    const {data: defaultsData} = useBundleWasteDefaults({bundleId});

    useEffect(() => {
        if (waste) {
            const formData = wasteToWasteForm(waste);
            resetForm(formData);
        } else {
            if (defaultsData) {
                const formData = getFormValues();
                formData.manifestTypeId = defaultsData.manifestType?.id ?? null;
                formData.packingModeId = defaultsData.packingMode?.id ?? null;
                resetForm(formData);
            }
        }
    }, [waste, resetForm, getFormValues, defaultsData]);

    const {data: self} = useSelf();
    const bundleTqrv = useBundleById(bundleId ? {bundleId} : undefined);
    const bundleDestination = bundleTqrv.data?.destination;
    const {data: continuingWastesInBundle} = useBundleContinuingWastes(bundleId ? {bundleId} : undefined);
    const canChangeDestination = (!!self?.inspectorFor || !!self?.supervisorFor) &&
        (isEmpty(continuingWastesInBundle) || continuingWastesInBundle?.[0]?.id === props.waste?.id);
    // const canChangeDestination = true;
    // const {data: isMyBundle} = useIsMyBundle({bundleId});

    const {mutateAsync: stampWaste} = useStampWasteMutation();
    const stampOnloaded = useCallback(({wasteId}: { wasteId: string }) => stampWaste({
        food: {wasteId, flavor: WasteStampFlavor.enum.ONLOADED}
    }), [stampWaste]);

    const {mutateAsync: performSpawn} = useSpawnWasteMutation();
    const {mutateAsync: performPatch} = usePatchWasteMutation();

    const onValidSubmitPatch = useCallback((form: WasteForm) => {
        return performPatch({
            wasteId: form.id!,
            patch: makeWastePatch({prev: props.waste!, next: form})
        }).then(() => {
            if (afterPatch) {
                afterPatch();
            }
        })
    }, [afterPatch, performPatch, props.waste]);

    // we used to only stamp onloaded if it is our bundle.
    // this causes problems with mochikomi
    const onValidSubmitSpawn = useCallback((form: WasteForm) => {
        return performSpawn({bundleId, spawnFood: wasteFormToNewWasteFood(form)})
            .then(({waste}) => {
                const nextStep$ = stampOnloaded({wasteId: waste.id});
                return nextStep$.then(() => {
                    return {waste};
                })
            })
            .then(newb => {
                if (afterSpawn) {
                    afterSpawn(newb);
                }
            });
    }, [afterSpawn, bundleId, performSpawn, stampOnloaded]);

    const bomb = useBomb();

    const onSubmit = useCallback((evt: FormEvent<HTMLFormElement>) => {
        const handler = handleSubmit(onValidSubmit ??
            (waste ? onValidSubmitPatch : onValidSubmitSpawn)
        );
        handler(evt).catch(bomb)
    }, [bomb, handleSubmit, onValidSubmit, onValidSubmitPatch, onValidSubmitSpawn, waste]);

    const {field: flavorField}
        = useController({
        control,
        name: "flavor",
        defaultValue: waste?.flavor,
        rules: {required: true}
    });
    const [flavorChooserOpen, setFlavorChooserOpen]
        = useState<boolean>(!flavorField.value);

    const flavorId = flavorField.value?.id ?? waste?.flavor.id;
    const flavorTqrv
        = useWasteFlavorById(flavorId ? {wasteFlavorId: flavorId} : undefined);
    const flavor = flavorTqrv.data;

    useEffect(() => {
        if (onFlavorChange) {
            const activeFlavor = flavor ?? waste?.flavor;
            if (activeFlavor) {
                onFlavorChange(activeFlavor);
            }
        }
    }, [onFlavorChange, flavor, waste?.flavor]);

    const {field: invManiLinkedField}
        = useController({control, name: "invManiLinked"});

    const manifestDisabled = readonly || invManiLinkedField.value;
    const commonFieldSx = {
        p: 1.2,
        m: 0.8,
    }
    const mandatorySx = {
        ...commonFieldSx,
        borderWidth: 2,
        borderColor: "primary.700",
        borderStyle: "solid"
    };
    const orientation = useOrientation();

    const updateLinkCallback = useCallback((invFieldValueStr: string) => {
        if (flavor) {
            updateLink({flavor, units, getFormValues, setFormValue, invFieldValueStr});
        }
    }, [flavor, getFormValues, setFormValue, units]);

    const possibleFlavors: InfallibleUseResult<WasteFlavorStub[]> = possibleFlavorsIn
        ? successUseResult(possibleFlavorsIn)
        : pendingUseResult;

    const onFlavorChanged = useCallback((choice: WasteFlavorStub | null) => {
        if (choice?.id !== flavor?.id) {
            // resetForm({
            //     ...getFormValues(),
            //     flavor: choice,
            // });
            setFormValue("flavor", choice, {
                shouldDirty: true,
            });
            if (choice && onFlavorChange) {
                onFlavorChange(choice);
            }
        }
        setFlavorChooserOpen(false);
    }, [flavor?.id, onFlavorChange, setFormValue]);

//    const baraPackingModeCode = "01";
    const fureconPackingModeCode = "07";
    const {data: packingModes} = usePackingModeArmap();
    const fureconPackingMode = packingModes?.byCode.get(fureconPackingModeCode);
//    const baraPackingMode = packingModes?.byCode.get(baraPackingModeCode);

    const bagCount = useWatch({
        control, name: "bags",
    });
    useEffect(() => {
        if (bagCount) {
            setFormValue("packingModeId", fureconPackingMode?.id ?? null);
        }
    }, [bagCount, fureconPackingMode?.id, setFormValue]);

    if (!!waste && !flavor) {
        return null;
    }

    return <Sheet>
        <form id="wasteEditor" onSubmit={(evt) => {
            onSubmit(evt);
        }}>
            <Stack direction="column" spacing={2} sx={{alignItems: "center"}}>
                <FlavorField control={control} readonly={readonly} setFlavorChooserOpen={setFlavorChooserOpen}/>
                <Stack direction={orientation === "landscape" ? "row" : "column"}
                       spacing={2} sx={{alignItems: "center", minWidth: "80%", minHeight: "80%"}}>
                    <Stack direction={orientation === "landscape" ? "row" : "column"} spacing={2}
                           sx={{alignItems: "center"}}>
                        <InvoiceFieldset control={control} readonly={readonly}
                                         formErrors={formErrors} flavor={flavor}
                                         updateLink={invManiLinkedField.value ? updateLinkCallback : undefined}
                                         mandatorySx={mandatorySx} commonFieldSx={commonFieldSx}/>
                        <InvManiLinkField value={invManiLinkedField.value} clearErrors={clearErrors}
                                          onChange={invManiLinkedField.onChange} readonly={readonly}/>
                        <ManifestFieldset control={control} disabled={manifestDisabled}
                                          formErrors={formErrors} flavor={flavor}
                                          isInvManiLinked={invManiLinkedField.value}
                                          mandatorySx={mandatorySx} commonFieldSx={commonFieldSx}/>
                    </Stack>

                    <Stack direction={orientation === "landscape" ? "column" : "row"} sx={{
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "space-evenly"
                    }}>
                        <PackingModeField control={control} readonly={readonly}/>
                        <BagsField control={control} readonly={readonly}/>
                        <BaraField control={control} readonly={readonly}/>
                    </Stack>

                    <Stack direction={orientation === "landscape" ? "column" : "row"}
                           spacing={3} sx={{
                        alignItems: "center",
                        justifyContent: "space-evenly"
                    }}>
                        <ManifestTypeField control={control} readonly={readonly}/>
                        <ManifestNumberField control={control} readonly={readonly}/>
                    </Stack>

                    <DestinationField control={control}
                                      readonly={readonly || !canChangeDestination}
                                      bundleDestination={bundleDestination}/>
                </Stack>

                {!readonly
                    ? <HCBox>
                        <Button type="submit" sx={{px: 4, py: 1.5}}
                                disabled={!isFormDirty || !isFormValid}>
                            {t("driver.doChange")}
                        </Button>
                    </HCBox>
                    : null}
            </Stack>
        </form>
        <Modal open={flavorChooserOpen}
               onClose={() => {
                   setFlavorChooserOpen(false);
               }}
               sx={{display: "flex", justifyContent: "center", alignItems: "center"}}>
            <Sheet sx={{
                maxWidth: "95%",
                borderRadius: "md",
                p: 3,
                boxShadow: "lg"
            }}>
                <WasteFlavorChooser
                    flavors={possibleFlavors}
                    currentSelection={flavorField.value ?? undefined}
                    onChosen={onFlavorChanged}/>
            </Sheet>
        </Modal>
    </Sheet>
}

